import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import useAppearance from '%evp_theme/brandbook/appearance/useAppearance';
import APPEARANCE_OPTIONS, {
    OPTION_ICON_SIZE,
    SELECTED_ICON_PATH,
    SELECTED_ICON_SIZE,
    findAppearanceOption,
} from '%evp_theme/brandbook/appearance/appearanceOptions';
import MaterialSymbol from '../MaterialSymbol';
import useLocaleNavigation, { INTERNATIONAL_COUNTRY_CODE } from '../../services/useLocaleNavigation';
import navigateTo from '../../services/navigateTo';
import MOBILE_FADE_IN_KEYFRAMES from '../animations';
import brandSurfaceInk from '../../services/brandSurfaceInk';
import {
    ACCOUNT_CHEVRON_COLOR,
    ACCOUNT_ITEM_RADIUS,
    ACCOUNT_ITEM_TEXT_COLOR,
    ACCOUNT_PANEL_METRICS,
    ACCOUNT_SELECTED_OPTION_COLOR,
    accountTypography,
    isAccountSessionItem,
    startsAccountSessionGroup,
} from '%evp_theme/brandbook/account/accountMenu';
import AccountRoleStrip from './AccountRoleStrip';
import buildAccountActions, { accountActionLinkProps } from './buildAccountActions';
import {
    destinationIconColor,
    MY_TICKETS_TREATMENT,
    MANAGE_EVENTS_TREATMENT,
    USER_SETTINGS_TREATMENT,
} from './accountDestinationTreatments';
import { accountShape } from './propTypes';
import {
    AppearanceIcon,
    APPEARANCE_OPTION_LABEL_SX,
    APPEARANCE_SELECTED_MARK_SLOT_SX,
    labelAppearance,
    labelAppearanceOption,
} from './AppearanceSelector';
import FOCUS_RING_SX, { createFieldFocusHalo } from '../focusRingSx';
import VISUALLY_HIDDEN_SX from '../visuallyHiddenSx';
import {
    CONTROL_BORDER,
    HOVERED_ROW_SX,
    SUBTLE_BORDER_COLOR,
} from '../highContrastSx';

const PANEL_SX = {
    display: { xs: 'flex', lg: 'none' },
    flexDirection: 'column',
    borderTop: '1px solid',
    borderColor: SUBTLE_BORDER_COLOR,
    bgcolor: 'background.default',
    boxShadow: ACCOUNT_PANEL_METRICS.shadow,
    animation: 'redesignMobileMenuFadeIn 0.25s ease',
    '@keyframes redesignMobileMenuFadeIn': MOBILE_FADE_IN_KEYFRAMES,
};

const NAV_SECTION_SX = {
    px: 2.5,
    pt: 2,
    pb: 1,
};

export const NAV_ITEM_BASE_SX = theme => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1,
    py: 1.5,
    color: ACCOUNT_ITEM_TEXT_COLOR,
    ...accountTypography(theme, 'panelRow'),
    textDecoration: 'none',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'color 0.15s',
});

const {
    rowIconSize: NAV_ICON_SIZE,
    chevronSize: NAV_CHEVRON_ICON_SIZE,
    appearanceOptionMinHeight: APPEARANCE_OPTION_MIN_HEIGHT,
} = ACCOUNT_PANEL_METRICS;

const NAV_ITEM_SEPARATOR_SX = {
    height: '1px',
    bgcolor: SUBTLE_BORDER_COLOR,
};

const NAV_ITEM_LABEL_SX = {
    flex: 1,
    transition: 'color 0.15s',
};

const NAV_ITEM_CHEVRON_SX = {
    color: ACCOUNT_CHEVRON_COLOR,
    display: 'flex',
    transition: 'color 0.15s',
};

const LOCALE_ROW_SX = {
    bgcolor: 'brandSurface.main',
    px: 2.5,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
};

const LOCALE_CELL_SX = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    flex: 1,
    minWidth: 0,
};

const LOCALE_ICON_SX = {
    color: theme => brandSurfaceInk(theme, 0.5),
    display: 'flex',
    flexShrink: 0,
};

export const LOCALE_SELECT_SX = theme => ({
    flex: 1,
    minWidth: 0,
    border: CONTROL_BORDER(theme),
    borderColor: brandSurfaceInk(theme, 0.2),
    borderRadius: 2,
    bgcolor: alpha(theme.palette.brandSurface.contrastText, 0.08),
    py: 0.75,
    px: 1.5,
    fontSize: theme.typography.pxToRem(14),
    color: 'brandSurface.contrastText',
    outline: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
    textTransform: 'capitalize',
    '& option': {
        color: 'text.primary',
        bgcolor: 'background.default',
        textTransform: 'capitalize',
    },
    '&:focus': {
        borderColor: 'brandSurface.focus',
        boxShadow: createFieldFocusHalo(theme, {
            color: theme.palette.brandSurface.focus,
            haloOpacity: 0.2,
        }),
    },
});

const LOCALE_DIVIDER_SX = {
    height: 20,
    width: '1px',
    bgcolor: theme => brandSurfaceInk(theme, 0.15),
    flexShrink: 0,
};

const ACCOUNT_SECTION_DIVIDER_SX = {
    ...NAV_ITEM_SEPARATOR_SX,
    mx: 1,
    my: 0.5,
};

const ACCOUNT_ITEM_INTERACTION_SX = {
    borderRadius: `${ACCOUNT_ITEM_RADIUS}px`,
    transition: 'background-color 0.15s',
    '&:focus': { color: ACCOUNT_ITEM_TEXT_COLOR },
    '&:hover': HOVERED_ROW_SX,
    '&:active': { opacity: 0.7 },
};

const ACCOUNT_ITEM_SX = [NAV_ITEM_BASE_SX, ACCOUNT_ITEM_INTERACTION_SX];

function buildAccountIconSx(iconColor) {
    return {
        color: destinationIconColor(iconColor),
        display: 'flex',
        flexShrink: 0,
    };
}

const APPEARANCE_TOGGLE_SX = [
    NAV_ITEM_BASE_SX,
    ACCOUNT_ITEM_INTERACTION_SX,
    {
        width: '100%',
        border: 'none',
        bgcolor: 'transparent',
        fontFamily: 'inherit',
        textAlign: 'start',
    },
];

const APPEARANCE_ICON_SX = buildAccountIconSx(USER_SETTINGS_TREATMENT.iconColor);

const APPEARANCE_VALUE_SX = {
    minWidth: 0,
    maxWidth: '50%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'text.secondary',
    fontWeight: 400,
};

function buildDisclosureChevronSx(expanded) {
    return {
        ...NAV_ITEM_CHEVRON_SX,
        transition: 'color 0.15s, transform 0.15s',
        transform: expanded ? 'rotate(180deg)' : 'none',
    };
}

const APPEARANCE_OPTIONS_SX = {
    ml: theme => `calc(${NAV_ICON_SIZE}px + ${theme.spacing(1.5)})`,
    pb: 1,
};

const APPEARANCE_OPTION_SX = theme => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    minHeight: APPEARANCE_OPTION_MIN_HEIGHT,
    px: 1,
    borderRadius: `${ACCOUNT_ITEM_RADIUS}px`,
    color: ACCOUNT_ITEM_TEXT_COLOR,
    ...accountTypography(theme, 'item'),
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background-color 0.15s',
    '&:hover': HOVERED_ROW_SX,
    '&:has(input:focus-visible)': FOCUS_RING_SX,
});

const APPEARANCE_OPTION_SELECTED_SX = [
    APPEARANCE_OPTION_SX,
    {
        bgcolor: ACCOUNT_SELECTED_OPTION_COLOR,
        fontWeight: 600,
        '&:hover': { bgcolor: ACCOUNT_SELECTED_OPTION_COLOR },
    },
];

function AppearanceRow({ choice, onChange }) {
    const [expanded, setExpanded] = useState(false);
    const baseId = useId();
    const labelId = `${baseId}-label`;
    const optionsId = `${baseId}-options`;
    const current = findAppearanceOption(choice);

    return (
        <Box data-testid="redesign-mobile-menu-appearance-row">
            <Box
                component="button"
                type="button"
                aria-expanded={expanded}
                aria-controls={optionsId}
                onClick={() => setExpanded(isExpanded => !isExpanded)}
                sx={APPEARANCE_TOGGLE_SX}
                data-testid="redesign-mobile-menu-appearance-toggle"
            >
                <Box sx={APPEARANCE_ICON_SX}>
                    <AppearanceIcon path={current.iconPath} size={NAV_ICON_SIZE} />
                </Box>
                <Box component="span" id={labelId} sx={NAV_ITEM_LABEL_SX}>{labelAppearance()}</Box>
                <Box component="span" sx={APPEARANCE_VALUE_SX} data-testid="redesign-mobile-menu-appearance-value">
                    {labelAppearanceOption(current)}
                </Box>
                <Box sx={buildDisclosureChevronSx(expanded)}>
                    <MaterialSymbol name="expand_more" size={NAV_CHEVRON_ICON_SIZE} />
                </Box>
            </Box>
            <Box
                role="radiogroup"
                id={optionsId}
                aria-labelledby={labelId}
                hidden={!expanded}
                sx={APPEARANCE_OPTIONS_SX}
                data-testid="redesign-mobile-menu-appearance-options"
            >
                {APPEARANCE_OPTIONS.map((option) => {
                    const checked = option.choice === choice;

                    return (
                        <Box
                            component="label"
                            key={option.choice}
                            sx={checked ? APPEARANCE_OPTION_SELECTED_SX : APPEARANCE_OPTION_SX}
                            data-testid={`redesign-mobile-menu-appearance-option-${option.choice}`}
                        >
                            <Box
                                component="input"
                                type="radio"
                                name={optionsId}
                                value={option.choice}
                                checked={checked}
                                onChange={() => onChange(option.choice)}
                                sx={VISUALLY_HIDDEN_SX}
                            />
                            <AppearanceIcon path={option.iconPath} size={OPTION_ICON_SIZE} />
                            <Box component="span" sx={APPEARANCE_OPTION_LABEL_SX}>
                                {labelAppearanceOption(option)}
                            </Box>
                            <Box component="span" sx={APPEARANCE_SELECTED_MARK_SLOT_SX}>
                                {checked ? (
                                    <AppearanceIcon path={SELECTED_ICON_PATH} size={SELECTED_ICON_SIZE} />
                                ) : null}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

AppearanceRow.propTypes = {
    choice: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

function MobileMenu({
    open,
    headerProps,
    translations,
    onNavigate = () => {},
    navigate = navigateTo,
}) {
    const {
        locale,
        country,
        buyerSpaUrl,
        adminSpaUrl,
        availableLocales,
        availableCountries,
        account = null,
    } = headerProps;

    const { changeCountry, changeLocale } = useLocaleNavigation({ locale, country, navigate });
    const appearance = useAppearance();

    if (!open) {
        return null;
    }

    const handleCountryChange = (event) => {
        changeCountry(event.target.value);
    };

    const handleLanguageChange = (event) => {
        changeLocale(event.target.value);
    };

    const accountActions = account === null ? [] : buildAccountActions({
        myTicketsUrl: buyerSpaUrl,
        manageEventsUrl: adminSpaUrl,
        userSettingsUrl: account.settingsUrl,
        logoutUrl: account.logoutUrl,
        stopImpersonatingUrl: account.stopImpersonatingUrl,
        translations,
    });
    const firstSessionActionIndex = accountActions.findIndex(action => isAccountSessionItem(action.key));

    return (
        <Box sx={PANEL_SX} data-testid="redesign-mobile-menu-panel">
            {account === null ? (
                <Box sx={NAV_SECTION_SX}>
                    {buyerSpaUrl ? (
                        <Box
                            component="a"
                            href={buyerSpaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onNavigate}
                            sx={ACCOUNT_ITEM_SX}
                            data-testid="redesign-mobile-menu-my-tickets"
                        >
                            <Box sx={buildAccountIconSx(MY_TICKETS_TREATMENT.iconColor)}>
                                <MaterialSymbol name={MY_TICKETS_TREATMENT.icon} size={NAV_ICON_SIZE} />
                            </Box>
                            <Box sx={NAV_ITEM_LABEL_SX}>{translations.myTickets}</Box>
                            <Box sx={NAV_ITEM_CHEVRON_SX}>
                                <MaterialSymbol name="chevron_right" size={NAV_CHEVRON_ICON_SIZE} />
                            </Box>
                        </Box>
                    ) : null}

                    {buyerSpaUrl && adminSpaUrl ? (
                        <Box sx={NAV_ITEM_SEPARATOR_SX} data-testid="redesign-mobile-menu-separator" />
                    ) : null}

                    {adminSpaUrl ? (
                        <Box
                            component="a"
                            href={adminSpaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onNavigate}
                            sx={ACCOUNT_ITEM_SX}
                            data-testid="redesign-mobile-menu-manage-events"
                        >
                            <Box sx={buildAccountIconSx(MANAGE_EVENTS_TREATMENT.iconColor)}>
                                <MaterialSymbol name={MANAGE_EVENTS_TREATMENT.icon} size={NAV_ICON_SIZE} />
                            </Box>
                            <Box sx={NAV_ITEM_LABEL_SX}>{translations.manageEvents}</Box>
                            <Box sx={NAV_ITEM_CHEVRON_SX}>
                                <MaterialSymbol name="chevron_right" size={NAV_CHEVRON_ICON_SIZE} />
                            </Box>
                        </Box>
                    ) : null}

                    {appearance.enabled && (buyerSpaUrl || adminSpaUrl) ? (
                        <Box sx={NAV_ITEM_SEPARATOR_SX} data-testid="redesign-mobile-menu-appearance-separator" />
                    ) : null}

                    {appearance.enabled ? (
                        <AppearanceRow
                            choice={appearance.choice}
                            onChange={appearance.setChoice}
                        />
                    ) : null}
                </Box>
            ) : (
                <>
                    <AccountRoleStrip
                        role={account.role}
                        fullName={account.fullName}
                        email={account.email}
                        translations={translations}
                    />
                    <Box sx={NAV_SECTION_SX} data-testid="redesign-mobile-menu-account-section">
                        {accountActions.map((action, index) => (
                            <React.Fragment key={action.key}>
                                {index === firstSessionActionIndex && appearance.enabled ? (
                                    <AppearanceRow
                                        choice={appearance.choice}
                                        onChange={appearance.setChoice}
                                    />
                                ) : null}
                                {startsAccountSessionGroup(accountActions, index) ? (
                                    <Box
                                        sx={ACCOUNT_SECTION_DIVIDER_SX}
                                        data-testid="redesign-mobile-menu-account-divider"
                                    />
                                ) : null}
                                <Box
                                    {...accountActionLinkProps(action)}
                                    onClick={onNavigate}
                                    sx={ACCOUNT_ITEM_SX}
                                    data-testid={`redesign-mobile-menu-account-${action.key}`}
                                >
                                    <Box sx={buildAccountIconSx(action.iconColor)}>
                                        <MaterialSymbol name={action.icon} size={NAV_ICON_SIZE} />
                                    </Box>
                                    <Box sx={NAV_ITEM_LABEL_SX}>{action.label}</Box>
                                    <Box sx={NAV_ITEM_CHEVRON_SX}>
                                        <MaterialSymbol
                                            name="chevron_right"
                                            size={NAV_CHEVRON_ICON_SIZE}
                                        />
                                    </Box>
                                </Box>
                            </React.Fragment>
                        ))}
                    </Box>
                </>
            )}

            <Box sx={LOCALE_ROW_SX} data-testid="redesign-mobile-menu-locale-row">
                <Box sx={LOCALE_CELL_SX}>
                    <Box sx={LOCALE_ICON_SX}>
                        <MaterialSymbol name="location_on" size={16} />
                    </Box>
                    <Box
                        component="select"
                        value={country || INTERNATIONAL_COUNTRY_CODE}
                        onChange={handleCountryChange}
                        aria-label={translations.country}
                        sx={LOCALE_SELECT_SX}
                        data-testid="redesign-mobile-menu-country-select"
                    >
                        {availableCountries.map(option => (
                            <option
                                key={option.code || 'international'}
                                value={option.code || INTERNATIONAL_COUNTRY_CODE}
                            >
                                {option.name}
                            </option>
                        ))}
                    </Box>
                </Box>

                <Box sx={LOCALE_DIVIDER_SX} data-testid="redesign-mobile-menu-locale-divider" />

                <Box sx={LOCALE_CELL_SX}>
                    <Box sx={LOCALE_ICON_SX}>
                        <MaterialSymbol name="translate" size={16} />
                    </Box>
                    <Box
                        component="select"
                        value={locale}
                        onChange={handleLanguageChange}
                        aria-label={translations.language}
                        sx={LOCALE_SELECT_SX}
                        data-testid="redesign-mobile-menu-language-select"
                    >
                        {availableLocales.map(option => (
                            <option key={option.code} value={option.code}>
                                {option.name}
                            </option>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

MobileMenu.propTypes = {
    open: PropTypes.bool.isRequired,
    onNavigate: PropTypes.func,
    navigate: PropTypes.func,
    headerProps: PropTypes.shape({
        locale: PropTypes.string.isRequired,
        country: PropTypes.string,
        buyerSpaUrl: PropTypes.string,
        adminSpaUrl: PropTypes.string,
        account: accountShape,
        availableLocales: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
            }),
        ).isRequired,
        availableCountries: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string.isRequired,
            }),
        ).isRequired,
    }).isRequired,
    translations: PropTypes.shape({
        myTickets: PropTypes.string.isRequired,
        manageEvents: PropTypes.string.isRequired,
        userSettings: PropTypes.string.isRequired,
        accountLogout: PropTypes.string.isRequired,
        stopImpersonating: PropTypes.string.isRequired,
        country: PropTypes.string.isRequired,
        language: PropTypes.string.isRequired,
        accountSignedInAsAdministrator: PropTypes.string.isRequired,
        accountSignedInAsOperator: PropTypes.string.isRequired,
        accountSignedInAsBuyer: PropTypes.string.isRequired,
    }).isRequired,
};

export default MobileMenu;
